import { BorderStyle, Box, useToggle } from '@factorialco/deprecated-design-system'
import type moment from 'moment'

import { observer } from 'mobx-react'

import { dateToString } from 'modules/core/lib/dates'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import AddShiftContextualButton from 'modules/shift_management/components/ShiftManagement/AddShiftContextualButton'
import EmptyShiftCell from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/EmptyShiftCell'
import useCopyShift from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/utils/useCopyShift'
import useMoveShift from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/utils/useMoveShift'
import useDateRangeSelection from 'modules/shift_management/hooks/useDateRangeSelection'
import type { DragItem } from 'modules/shift_management/hooks/useDragAndDrop'
import { useDropShift } from 'modules/shift_management/hooks/useDragAndDrop'
import { usePreview } from 'modules/shift_management/hooks/usePreview'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import {
  useSelectionDispatch,
  useSelectionState,
} from 'modules/shift_management/hooks/useSelection'
import type ShiftsRow from 'modules/shift_management/store/internal/models/ShiftsRow'

import DropItem from './DropItem'

interface EmptyCellActionsProps {
  shiftsRow: ShiftsRow
  date: moment.Moment
  isOver: boolean
  isSelected?: boolean
  contractVersions: ContractVersions
}

const EmptyCellActions = ({
  shiftsRow,
  date,
  isOver,
  isSelected = false,
  contractVersions,
}: EmptyCellActionsProps) => {
  const [isHovering, toggleHovering] = useToggle(false)
  const dispatchSelection = useSelectionDispatch()
  const { showEmptyShiftCell } = useSelectionState()
  const [{ view }] = useSchedule()
  const { moveShift } = useMoveShift(contractVersions)
  const { copyShift } = useCopyShift(contractVersions)
  const [{ shifts: previewShifts }] = usePreview()
  const hasPreview = !!previewShifts.length
  // Hide the selected empty cells when there is a preview
  const isHidden = isSelected && hasPreview

  const formattedDate = dateToString(date, 'backend')
  const employee = shiftsRow.employees[0]
  const employeeIds = employee ? [employee.id] : []

  const daysByEmployee = useDateRangeSelection(employeeIds, formattedDate, formattedDate, '', true)

  const {
    isOverCurrent: isOverMoveOption,
    drop: dropOverMoveShift,
    originalItem,
  } = useDropShift({
    shiftsRow,
    date,
    action: async (item: DragItem) => {
      await moveShift({
        originShiftsRow: item.shiftsRow,
        newShiftsRow: shiftsRow,
        shift: item.shift!,
        unassignedShift: item.unassignedShift!,
        date,
        daysByEmployee,
        employeeId: view.isByAreas ? item.shift!.employeeId : shiftsRow.employees[0]!.id,
      })
    },
  })

  const { isOverCurrent: isOverCopyOption, drop: dropOverCopyShift } = useDropShift({
    shiftsRow,
    date,
    action: async (item: DragItem) => {
      await copyShift({
        originShiftsRow: item.shiftsRow!,
        newShiftsRow: shiftsRow,
        shift: item.shift!,
        date,
        employeeId: view.isByAreas ? item.shift!.employeeId : shiftsRow.employees[0]!.id,
        contractVersions,
      })
    },
  })

  const onSelect = () => {
    dispatchSelection({
      type: 'TOGGLE_DAY',
      day: date,
      shiftsRow,
      view,
    })
  }

  const isHoveringWhileDragging = isOver || isOverMoveOption || isOverCopyOption
  const flexDirection = isOver ? 'row' : 'column'
  const justifyContent = isOver ? 'flexStart' : 'flexEnd'

  if (!shiftsRow.canManage) return null

  const height = view.isDaily ? 'full' : view.cellMinHeight

  const cellHeight = showEmptyShiftCell && isSelected ? undefined : height

  const background =
    isHovering && !isHoveringWhileDragging && !showEmptyShiftCell ? 'grey100' : 'grey000White'

  const border: BorderStyle = {
    style: 'solid',
    width: 's2',
    color: 'grey800',
  }

  const borderStyle = isSelected && !showEmptyShiftCell ? border : undefined

  if (!employee) return null

  return (
    <Box
      // Can't use maxWidth prop directly because gamma does not have s170 as a dimension size
      // which is necessary to accommodate the new cell design
      height={cellHeight}
      background={background}
      cursor="pointer"
      border={{ all: borderStyle }}
      borderRadius={{ all: 'abs008' }}
      onClick={onSelect}
      onMouseEnter={toggleHovering(true)}
      onMouseLeave={toggleHovering(false)}
      flexDirection={flexDirection}
      gap="s12"
      justifyContent={justifyContent}
      style={{ display: isHidden ? 'none' : 'flex' }}
    >
      {showEmptyShiftCell && isSelected && (
        <EmptyShiftCell employee={employee} contractVersions={contractVersions} />
      )}
      {isOver && (
        <>
          <DropItem ref={dropOverMoveShift} isOver={isOverMoveOption} icon="move-shifts" />
          {!view.isByAreas && view.isWeekly && !!originalItem.shift && (
            <DropItem ref={dropOverCopyShift} isOver={isOverCopyOption} icon="duplicate" />
          )}
        </>
      )}
      <AddShiftContextualButton
        date={date}
        shiftsRow={shiftsRow}
        contractVersions={contractVersions}
      />
    </Box>
  )
}

export default observer(EmptyCellActions)
